import { Modal } from "@/components/ui/modal";
import { Calendar } from "lucide-react";
import DOMPurify from "dompurify";

interface BlogModalProps {
  isOpen: boolean;
  onClose: () => void;
  blog: {
    id: string;
    title: string;
    content: string;
    date?: string;
  } | null;
}

// Sanitize HTML content to prevent XSS attacks
function sanitizeHtml(html: string): string {
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'b', 'i', 'em', 'strong', 'a', 'ul', 'ol', 'li', 
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code', 'pre',
      'span', 'div', 'img'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'class'],
    ALLOW_DATA_ATTR: false,
  });
  
  // Ensure all links have proper security attributes
  return clean.replace(
    /<a /g, 
    '<a target="_blank" rel="noopener noreferrer" '
  );
}

export function BlogModal({ isOpen, onClose, blog }: BlogModalProps) {
  if (!blog) return null;

  // Sanitize the blog content before rendering
  const sanitizedContent = sanitizeHtml(blog.content);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="space-y-5 animate-[fadeInScale_0.25s_ease-out]">
        {/* Blog Date */}
        {blog.date && (
          <div className="flex items-center gap-2 text-sm text-emerald-400/90">
            <Calendar className="w-4 h-4" />
            <time>{blog.date}</time>
          </div>
        )}

        {/* Blog Title */}
        <h2 className="text-2xl sm:text-3xl font-bold text-emerald-100 leading-tight">
          {blog.title}
        </h2>

        {/* Blog Content - Now sanitized */}
        <div className="rounded-xl bg-slate-800/40 border border-white/5 p-5 max-h-[60vh] overflow-y-auto">
          <div className="prose prose-invert prose-emerald max-w-none">
            <div 
              className="text-sm leading-relaxed text-slate-300 whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
